/**
 * Último acesso por pessoa (spec 017, FR-007/US4): equipe e logins de portal.
 * Em 2026-09-28 havia 128 logins de portal provisionados e nenhum jeito de
 * saber se alguém entrava. GET /users/last-seen é de admin; a API grava no
 * máximo a cada 5 min por pessoa, então "agora" tem essa resolução.
 */
import { queryOptions } from '@tanstack/react-query';
import type { MemberAccess } from '@erp/shared';
import { api } from '@/lib/api-client';
import { formatDateTime } from '@/lib/utils';

export function memberAccessQuery(enabled: boolean) {
  return queryOptions({
    queryKey: ['erp', 'users', 'last-seen'] as const,
    enabled,
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      const res = await api<MemberAccess[]>('/users/last-seen', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/** userId → último acesso NESTE tenant (null = membro que nunca entrou). */
export function lastSeenByUser(rows: MemberAccess[] | undefined): Map<number, string | Date | null> {
  return new Map((rows ?? []).map((r) => [r.userId, r.lastSeenAt]));
}

/**
 * Texto da coluna "Último acesso": data e hora, "Nunca entrou", ou "—" para
 * quem está na lista sem ser membro deste tenant — o atendente que só o
 * espelho do TiFlux trouxe (a lista é deste tenant desde F-108).
 */
export function lastSeenText(access: Map<number, string | Date | null>, userId: number): string {
  if (!access.has(userId)) return '—';
  const at = access.get(userId);
  return at ? formatDateTime(at) : 'Nunca entrou';
}

const WEEK_MS = 7 * 24 * 60 * 60_000;

/**
 * SC-003: "quantas pessoas da equipe e quantos gestores de cliente entraram
 * nesta semana" sem abrir o banco — últimos 7 dias, pelo papel neste tenant.
 */
export function weeklyAccessSummary(
  rows: MemberAccess[] | undefined,
  now: Date = new Date()
): { staff: number; staffTotal: number; clients: number; clientsTotal: number } {
  const recent = (r: MemberAccess) =>
    r.lastSeenAt !== null && now.getTime() - new Date(r.lastSeenAt).getTime() <= WEEK_MS;
  const all = rows ?? [];
  const staff = all.filter((r) => r.role !== 'client');
  const clients = all.filter((r) => r.role === 'client');
  return {
    staff: staff.filter(recent).length,
    staffTotal: staff.length,
    clients: clients.filter(recent).length,
    clientsTotal: clients.length,
  };
}
